import type { DeepPartial } from "./types";

export function use<T, R>(obj: T, fn: (arg: T) => R): R {
  return fn(obj);
}

export function deepEqual<T>(a: T, b: T): boolean {
  if (a === b) return true;

  if (typeof a !== typeof b) return false;

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }

  if (a && b && typeof a === "object") {
    const aObj = a as Record<string, unknown>;
    const bObj = b as Record<string, unknown>;
    const aKeys = Object.keys(aObj);
    const bKeys = Object.keys(bObj);
    if (aKeys.length !== bKeys.length) return false;

    for (const key of aKeys) {
      if (!deepEqual(aObj[key], bObj[key])) return false;
    }
    return true;
  }

  return false;
}

export function repeat<T>(times: number, fn: (index: number) => T): T[] {
  const result: T[] = [];
  for (let i = 0; i < times; i++) {
    result.push(fn(i));
  }
  return result;
}

/**
 * Removes all undefined properties from an object (not recursive).
 * @param obj The object to clean.
 * @returns A new object without undefined properties.
 */
export function cleanObject<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined)) as Partial<T>;
}

export const NOT_FOUND = Symbol("not_found");
/**
 * Finds an object in a list that matches a predicate and returns a selected value from it, or NOT_FOUND if no match is found.
 * @param list
 * @param predicate
 * @param selector
 * @returns The selected value from the found object, or NOT_FOUND if no match is found.
 */
export function findObject<T, R>(
  list: T[],
  predicate: (item: T) => boolean,
  selector: (item: T) => R
): R | typeof NOT_FOUND {
  for (const item of list) {
    if (predicate(item)) {
      return selector(item);
    }
  }
  return NOT_FOUND;
}

export function isNotFound<T>(value: T | typeof NOT_FOUND): value is typeof NOT_FOUND {
  return value === NOT_FOUND;
}

/**
 * Finds an object in a list that matches a predicate and returns a selected value from it, or NOT_FOUND if no match is found.
 * This version is simpler when you don't need a separate predicate and selector.
 * @param list
 * @param fn  A function that takes an item and returns the desired value if it's a match, or NOT_FOUND if it doesn't match.
 * @returns  The value returned by the function for the first matching item, or NOT_FOUND if no match is found.
 */
export function findObjectSimple<T, R>(list: T[], fn: (item: T) => R | typeof NOT_FOUND): R | typeof NOT_FOUND {
  for (const item of list) {
    const result = fn(item);
    if (result !== NOT_FOUND) {
      return result;
    }
  }
  return NOT_FOUND;
}

/**
 * Deep-merge `source` into `target` in-place.
 * Only defined (non-undefined) values from `source` are applied.
 * Nested objects are merged recursively; scalar values are overwritten.
 */
export function deepMerge<T extends object>(target: T, source: DeepPartial<T>): T {
  for (const key of Object.keys(source) as (keyof DeepPartial<T>)[]) {
    const sourceVal = source[key];
    if (sourceVal === undefined || sourceVal === null) continue;

    const targetVal = target[key];
    if (
      typeof sourceVal === "object" &&
      !Array.isArray(sourceVal) &&
      typeof targetVal === "object" &&
      !Array.isArray(targetVal) &&
      targetVal !== null
    ) {
      deepMerge(targetVal as object, sourceVal as DeepPartial<object>);
    } else {
      (target as any)[key] = sourceVal;
    }
  }
  return target;
}
